import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MonthOverMonth } from "./monthOverMonth";

/**
 * The month-over-month move, as two facts in one column.
 *
 * Revenue change on top, in percent: whether the account grew. Margin change
 * underneath, in PERCENTAGE POINTS: whether the work got better. Points, not
 * percent, because a margin going 5% to 10% is "+100%" in percent-of-a-percent
 * and everyone reads that as ninety-five points of improvement.
 *
 * Direction is carried three ways -- an arrow, an explicit sign, and colour --
 * so it survives a colour-blind reader, a greyscale print and a screenshot.
 */
export function MomCell({ change }: { change: MonthOverMonth | undefined }) {
  if (!change || (change.revenuePct === null && change.marginPoints === null)) {
    return (
      <span
        className="text-muted-foreground"
        title={change?.reason ?? "No prior month on record for this account"}
      >
        —
      </span>
    );
  }

  const { revenuePct, marginPoints, priorLabel } = change;
  // Exact zero is flat: an arrow either way would claim a direction.
  const Arrow =
    revenuePct === null || revenuePct === 0 ? Minus : revenuePct > 0 ? ArrowUp : ArrowDown;
  const tone =
    revenuePct === null || revenuePct === 0
      ? "text-muted-foreground"
      : revenuePct > 0
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-destructive";

  return (
    <span
      className="inline-flex flex-col items-end leading-tight"
      title={priorLabel ? `Compared with ${priorLabel}` : undefined}
    >
      <span className={cn("inline-flex items-center gap-1 tabular-nums", tone)}>
        <Arrow className="size-3" aria-hidden />
        {revenuePct === null ? (
          <span className="text-muted-foreground" title="The prior month had no revenue">
            —
          </span>
        ) : (
          `${revenuePct > 0 ? "+" : ""}${revenuePct.toFixed(1)}%`
        )}
      </span>
      <span className="text-[11px] text-muted-foreground tabular-nums">
        {marginPoints === null
          ? "— pts margin"
          : `${marginPoints > 0 ? "+" : ""}${marginPoints.toFixed(1)} pts margin`}
      </span>
    </span>
  );
}
